import React, { useEffect, useMemo, useState } from "react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import Skeleton from "@mui/material/Skeleton";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import { formatHelper } from "helper/format";

const CARD_GRADIENT = "linear-gradient(153deg, #4084EF 2%, #161442 98%)";
const BADGE_TEXT = "#1D275F";
const CTA_TEXT = "#28438A";

const PERIOD_LABEL = {
  YEARLY: "Yearly",
  HALF_YEARLY: "Half-Yearly",
};

const BASIC_BENEFITS = [
  "Avg. 4+ Investment-Worthy Stocks to invest in month",
  "Positional - Short term",
  "Tailored Stock picks across NSE",
  "AI-Researched stock selection",
  "Real-Time buy, and sell alerts",
  "24 x 7 stock performance monitoring",
];

const PLUS_BENEFITS = [
  "Avg. 10+ Investment-Worthy Stocks to invest in monthly",
  "Positional- Short & Long term",
  "AI-Researched stock selection",
  "Real-Time Booking and Exit alerts",
  "24 x 7 stock performance monitoring",
];

const PREMIUM_BENEFITS = [
  "Avg. 10+ Investment-Worthy Stocks to invest in monthly",
  "Positional- Short & Long term",
  "AI-Researched stock selection",
  "Real-Time Booking and Exit alerts",
  "24 x 7 stock performance monitoring",
];

const TIER_ORDER = ["BASIC", "PLUS", "PREMIUM"];
const TIER_LABEL = { BASIC: "Basic", PLUS: "Plus", PREMIUM: "Premium" };

const normalizeTier = (name = "") => {
  const n = String(name).toUpperCase();
  if (n.includes("PREMIUM")) return "PREMIUM";
  if (n.includes("PLUS")) return "PLUS";
  if (n.includes("BASIC")) return "BASIC";
  return null;
};

const FALLBACK_PLANS = [
  {
    tier: "BASIC",
    planFrequency: "YEARLY",
    planPrice: 6999,
    planCode: "JOS_BASIC_Y",
    audience: "For Beginners",
    label: "Basic",
    cta: "Unlock Basic",
    benefits: BASIC_BENEFITS,
    gstNote: "Incl. GST",
  },
  {
    tier: "BASIC",
    planFrequency: "HALF_YEARLY",
    planPrice: 3499,
    planCode: "JOS_BASIC_H",
    audience: "For Beginners",
    label: "Basic",
    cta: "Unlock Basic",
    benefits: BASIC_BENEFITS,
    gstNote: "Incl. GST",
  },
  {
    tier: "PLUS",
    planFrequency: "YEARLY",
    planPrice: 19999,
    planCode: "JOS_PLUS_Y",
    audience: "For Investors",
    label: "Plus",
    cta: "Unlock Plus",
    benefits: PLUS_BENEFITS,
    gstNote: "Incl. GST",
  },
  {
    tier: "PLUS",
    planFrequency: "HALF_YEARLY",
    planPrice: 8899,
    planCode: "JOS_PLUS_H",
    audience: "For Investors",
    label: "Plus",
    cta: "Unlock Plus",
    benefits: PLUS_BENEFITS,
    gstNote: "Incl. GST",
  },
  {
    tier: "PREMIUM",
    planFrequency: "YEARLY",
    planPrice: 0,
    planCode: "JOS_PREMIUM_Y",
    audience: "For Experts",
    label: "Premium",
    cta: "Unlock Premium",
    benefits: PREMIUM_BENEFITS,
    gstNote: "Incl. GST",
  },
  {
    tier: "PREMIUM",
    planFrequency: "HALF_YEARLY",
    planPrice: 0,
    planCode: "JOS_PREMIUM_H",
    audience: "For Experts",
    label: "Premium",
    cta: "Unlock Premium",
    benefits: PREMIUM_BENEFITS,
    gstNote: "Incl. GST",
  },
];

/** Default UI when API is empty — Basic + Plus only (no Premium). */
const DEFAULT_FALLBACK_PLANS = FALLBACK_PLANS.filter((p) => p.tier !== "PREMIUM");

const PeriodToggle = ({ value, onChange, size = "md" }) => {
  const isMobile = size === "sm";
  // Desktop Figma: Yearly | Half-Yearly. Mobile: Half-Yearly | Yearly.
  const periods = isMobile ? ["HALF_YEARLY", "YEARLY"] : ["YEARLY", "HALF_YEARLY"];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        p: isMobile ? "3.75px" : "4px",
        borderRadius: 999,
        bgcolor: "#F3F4F6",
        border: isMobile ? "0.94px solid #E5E7EB" : "1px solid #E2E8F0",
        boxShadow: isMobile
          ? "0 9.375px 22.5px -9.375px rgba(0,0,0,0.06), 0 0.94px 1.875px rgba(0,0,0,0.04)"
          : "none",
        overflow: "hidden",
      }}
    >
      {periods.map((period) => {
        const active = value === period;
        return (
          <Box
            key={period}
            component="button"
            type="button"
            onClick={() => onChange(period)}
            sx={{
              border: 0,
              cursor: "pointer",
              borderRadius: 999,
              bgcolor: active ? "#fff" : "transparent",
              boxShadow: active ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              px: isMobile ? 1.5 : 3,
              py: isMobile ? 1 : 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              sx={{
                fontSize: isMobile ? 13 : 14,
                fontWeight: active ? 600 : 500,
                color: active ? "#0F172A" : "#475569",
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {PERIOD_LABEL[period]}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
};

const TierTabs = ({ value, onChange, tiers = ["BASIC", "PLUS"] }) => (
  <Box
    sx={{
      display: "flex",
      width: "100%",
      height: 42,
      p: "4px",
      borderRadius: 999,
      bgcolor: "rgba(255,255,255,0.6)",
      border: "1px solid #E5E7EB",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      overflow: "hidden",
    }}
  >
    {tiers.map((tier) => {
      const active = value === tier;
      return (
        <Box
          key={tier}
          component="button"
          type="button"
          onClick={() => onChange(tier)}
          sx={{
            flex: 1,
            border: 0,
            cursor: "pointer",
            borderRadius: 999,
            bgcolor: active ? "#3F81EB" : "transparent",
            boxShadow: active ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            sx={{
              fontSize: 14,
              fontWeight: active ? 600 : 500,
              color: active ? "#fff" : "#000",
              lineHeight: 1,
            }}
          >
            {TIER_LABEL[tier] || tier}
          </Text>
        </Box>
      );
    })}
  </Box>
);

const PlanCardSkeleton = ({ mobile = false }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      height: "100%",
      p: mobile ? 0 : 2.5,
      borderRadius: mobile ? 0 : "20px",
      bgcolor: mobile ? "transparent" : "#fff",
      border: mobile ? "none" : "1px solid #E2E8F0",
      boxShadow: mobile ? "none" : "0 4px 6px rgba(0,0,0,0.04)",
    }}
  >
    <Stack
      spacing={4}
      sx={{
        p: 2.5,
        height: "100%",
        minHeight: mobile ? 360 : 420,
        borderRadius: "16px",
        bgcolor: "#F1F5F9",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5}>
          <Skeleton variant="rounded" width={110} height={28} sx={{ borderRadius: "14px" }} />
          {!mobile ? (
            <Skeleton variant="rounded" width={64} height={28} sx={{ borderRadius: "14px" }} />
          ) : null}
        </Stack>
        <Stack spacing={1}>
          <Skeleton variant="rounded" width="55%" height={mobile ? 32 : 36} />
          <Skeleton variant="text" width={72} height={16} />
        </Stack>
        <Skeleton variant="rounded" width="100%" height={42} sx={{ borderRadius: "14px" }} />
      </Stack>
      <Stack spacing={1.25}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Stack key={i} direction="row" spacing={1.25} alignItems="center">
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={`${70 - i * 8}%`} height={18} />
          </Stack>
        ))}
      </Stack>
    </Stack>
  </Box>
);

const OnestockPricingSkeleton = () => (
  <>
    <Stack
      spacing={8}
      alignItems="center"
      sx={{ display: { xs: "none", md: "flex" }, width: "100%" }}
    >
      <Skeleton variant="rounded" width={220} height={42} sx={{ borderRadius: 999 }} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 4,
          width: "100%",
          maxWidth: 900,
          mx: "auto",
          alignItems: "stretch",
        }}
      >
        <PlanCardSkeleton />
        <PlanCardSkeleton />
      </Box>
    </Stack>

    <Stack spacing={2} sx={{ display: { xs: "flex", md: "none" }, width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
          Pricing
        </Text>
        <Skeleton variant="rounded" width={172.5} height={39.5} sx={{ borderRadius: 999 }} />
      </Stack>
      <Stack
        spacing={2}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: "#fff",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
        }}
      >
        <Skeleton variant="rounded" width="100%" height={42} sx={{ borderRadius: 999 }} />
        <PlanCardSkeleton mobile />
      </Stack>
    </Stack>
  </>
);

const PlanCard = ({ plan, onUnlock, mobile = false }) => {
  const frequencyLabel = PERIOD_LABEL[plan.planFrequency] || plan.planFrequency;
  const price = formatHelper.currency(plan.planPrice);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        height: "100%",
        p: mobile ? 0 : 2.5,
        borderRadius: mobile ? 0 : "20px",
        bgcolor: mobile ? "transparent" : "#fff",
        border: mobile ? "none" : "1px solid #E2E8F0",
        boxShadow: mobile ? "none" : "0 4px 6px rgba(0,0,0,0.04)",
      }}
    >
      <Stack
        spacing={4}
        sx={{
          p: 2.5,
          height: "100%",
          borderRadius: "16px",
          background: CARD_GRADIENT,
          boxShadow: "0 10px 12px rgba(29,78,216,0.13)",
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Box sx={{ px: 2, py: 0.5, borderRadius: "14px", bgcolor: "#fff" }}>
              <Text
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: mobile ? "#3F81EB" : BADGE_TEXT,
                }}
              >
                {plan.audience}
              </Text>
            </Box>
            {!mobile ? (
              <Box sx={{ px: 2, py: 0.5, borderRadius: "14px", bgcolor: "#fff" }}>
                <Text sx={{ fontSize: 12, fontWeight: 500, color: BADGE_TEXT }}>
                  {plan.label}
                </Text>
              </Box>
            ) : null}
          </Stack>

          <Stack spacing={1}>
            <Text sx={{ color: "#fff", lineHeight: 1.3 }}>
              <Box component="span" sx={{ fontSize: mobile ? 28 : 32, fontWeight: 700 }}>
                {price}
              </Box>
              <Box component="span" sx={{ fontSize: 20, fontWeight: 400 }}>
                {` / ${frequencyLabel}`}
              </Box>
            </Text>
            <Text sx={{ fontSize: 12, color: "rgba(255,255,255,0.9)" }}>
              {plan.gstNote || "Incl. GST"}
            </Text>
          </Stack>

          <Button
            onClick={() => onUnlock?.(plan)}
            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />}
            sx={{
              width: "100%",
              height: 42,
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 16,
              color: CTA_TEXT,
              bgcolor: "#fff",
              boxShadow: "0 10px 12px rgba(37,99,235,0.2)",
              "&:hover": { bgcolor: "#fff" },
            }}
          >
            {plan.cta}
          </Button>
        </Stack>

        <Stack spacing={1.25}>
          {(plan.benefits || []).map((benefit) => (
            <Stack key={benefit} direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: "12px",
                  bgcolor: "#fff",
                  display: "grid",
                  placeItems: "center",
                  mt: "1px",
                }}
              >
                <CheckRoundedIcon sx={{ fontSize: 16, color: CTA_TEXT }} />
              </Box>
              <Text sx={{ fontSize: 14, color: "#fff", lineHeight: "21px" }}>{benefit}</Text>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

const OnestockPricingSection = ({ plans = [], loading = false, onUnlock }) => {
  const [period, setPeriod] = useState("YEARLY");
  const [tier, setTier] = useState("BASIC");

  const displayPlans = useMemo(() => {
    if (loading) return [];

    const fromApi = (plans || []).filter(
      (p) => p.planFrequency === "YEARLY" || p.planFrequency === "HALF_YEARLY",
    );
    if (fromApi.length >= 2) {
      return fromApi.map((p) => {
        const apiTier =
          normalizeTier(p.planName) ||
          normalizeTier(p.planCode) ||
          normalizeTier(p.tier) ||
          "BASIC";
        const fallback =
          FALLBACK_PLANS.find(
            (f) => f.planFrequency === p.planFrequency && f.tier === apiTier,
          ) || DEFAULT_FALLBACK_PLANS[0];
        const checklist = Array.isArray(p.planChecklist) ? p.planChecklist : [];
        return {
          ...fallback,
          ...p,
          tier: apiTier,
          planPrice: Number(p.planPrice ?? fallback.planPrice) || fallback.planPrice,
          label: TIER_LABEL[apiTier] || fallback.label,
          cta: `Unlock ${TIER_LABEL[apiTier] || fallback.label}`,
          benefits: checklist.length ? checklist : fallback.benefits,
        };
      });
    }
    return DEFAULT_FALLBACK_PLANS;
  }, [plans, loading]);

  const periodPlans = useMemo(
    () => displayPlans.filter((p) => p.planFrequency === period),
    [displayPlans, period],
  );

  const availableTiers = useMemo(() => {
    const present = new Set(periodPlans.map((p) => p.tier).filter(Boolean));
    const ordered = TIER_ORDER.filter((t) => present.has(t));
    return ordered.length ? ordered : ["BASIC", "PLUS"];
  }, [periodPlans]);

  useEffect(() => {
    if (!availableTiers.includes(tier)) {
      setTier(availableTiers[0] || "BASIC");
    }
  }, [availableTiers, tier]);

  const plansByTier = availableTiers
    .map(
      (t) =>
        periodPlans.find((p) => p.tier === t) ||
        (t !== "PREMIUM"
          ? DEFAULT_FALLBACK_PLANS.find(
              (f) => f.planFrequency === period && f.tier === t,
            )
          : null),
    )
    .filter(Boolean);

  const mobilePlan =
    plansByTier.find((p) => p.tier === tier) || plansByTier[0] || null;
  const columnCount = Math.max(plansByTier.length, 1);

  if (loading) {
    return <OnestockPricingSkeleton />;
  }

  return (
    <>
      {/* Desktop — cycle switcher + 2 or 3 cards from API */}
      <Stack
        spacing={8}
        alignItems="center"
        sx={{ display: { xs: "none", md: "flex" }, width: "100%" }}
      >
        <PeriodToggle value={period} onChange={setPeriod} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
            gap: 4,
            width: "100%",
            maxWidth: columnCount >= 3 ? "100%" : 900,
            mx: "auto",
            alignItems: "stretch",
          }}
        >
          {plansByTier.map((plan) => (
            <PlanCard key={plan.planCode || plan.tier} plan={plan} onUnlock={onUnlock} />
          ))}
        </Box>
      </Stack>

      {/* Mobile — Pricing + period + card(tiers + plan) */}
      <Stack spacing={2} sx={{ display: { xs: "flex", md: "none" }, width: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
            Pricing
          </Text>
          <PeriodToggle value={period} onChange={setPeriod} size="sm" />
        </Stack>

        <Stack
          spacing={2}
          sx={{
            p: 2.5,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <TierTabs value={tier} onChange={setTier} tiers={availableTiers} />
          {mobilePlan ? (
            <PlanCard mobile plan={mobilePlan} onUnlock={onUnlock} />
          ) : null}
        </Stack>
      </Stack>
    </>
  );
};

export default OnestockPricingSection;
